/**
 * /early（サイトの広告のページ）の「Continue with GitHub / Google」から来たとき、登録の画面を見せずにそのままプロバイダへ移る
 * （2026-10-22 site/early-inline-signup・founder「この kumodeck.com/early/ の中でサインアップさせないとだめだよ」・
 * 指示書 docs/teams/mgmt/tasks/early-inline-signup.md・結果 early-inline-signup-result.md）。「React に依存しない部分」= node --test で確かめる。
 *
 * 流れ: /early のボタン → <ダッシュボード>/signup?invite=<コード>&src=<ラベル>&via=github → （この画面は「GitHub を開いています…」だけ）
 *   → 今までと同じ OAuthButtons の押した時の処理（控え → POST /v1/developers/oauth/:provider/start）→ GitHub → /oauth/callback → ホーム
 *
 * WHY サイトから直接 start を呼ばないか（案 A をそのまま作らない）: PKCE の verifier と state は、戻ってくるダッシュボードのオリジンの
 *   sessionStorage に無いと exchange できない（devOAuth.ts の冒頭）。サイトで作ると戻り先を広げる（サーバーの redirects.ts の許可を増やす）か、
 *   セッションの鍵をオリジンの間で渡す新しい道が要る = どちらも守りを弱めるサーバーの変更。ダッシュボードを 1 回通るだけなら、
 *   サーバー・控え・招待コードの検査・$5 の付与は 1 文字も変わらない（同じ押した時の処理を呼ぶ）
 * WHY 埋め込み（iframe）にしないか（案 B）: ダッシュボードは frame-ancestors 'none'（public/_headers・クリックジャッキング）。
 *   お金の画面を他所の iframe に入れられるようにするのは守りを弱める。Google は iframe の中の同意画面を断る
 *
 * 守り:
 *   - via は自分のプロバイダの一覧（DEV_OAUTH_PROVIDERS）にある名前だけ。そのうえ platform/info の oauthProviders に無ければ始めない（普通の登録の画面）
 *   - 招待コードの検査は押した時と同じ（signupInviteError）。招待制で空・形が変なら始めない = 普通の登録の画面とエラーの 1 行
 *   - 読んだらすぐ URL から via を消す（invite と同じ effect）。WHY: GitHub の画面から「戻る」で帰ってきたとき、また自動で移ると抜け出せない
 *   - 自動で始めるのは 1 回だけ（失敗・戻る → 普通の登録の画面。メールでも登録できる）
 *   - 他人が via 付きの URL を踏ませても、往復はその人のタブの控え・その人のプロバイダのアカウント = 今のボタンを押させるのと同じ（新しい力は無い）
 */

import { isDevOAuthProvider, type DevOAuthProvider } from './devOAuth.ts';
import { signupInviteError, type InviteInput, type SignupInviteRequirement } from './inviteCode.ts';

/** URL の via（github / google / apple）。無い・知らない名前は null（普通の登録の画面） */
export function viaFromSearch(search: string): DevOAuthProvider | null {
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get('via');
  } catch {
    return null;
  }
  const v = (raw ?? '').trim().toLowerCase();
  return isDevOAuthProvider(v) ? v : null;
}

/** via を除いた search（他の値はそのまま）。'' なら ? も付けない（inviteCode.ts の searchWithoutInvite と同じ形） */
export function searchWithoutVia(search: string): string {
  const p = new URLSearchParams(search);
  p.delete('via');
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * 自動で始めるか。
 *   wait  … プロバイダの一覧がまだ読めていない（providers === null）
 *   start … 一覧にあり、招待コードも押した時の検査を通る → 押した時と同じ処理を 1 回
 *   skip  … via が無い・一覧に無い（読めなかった = 空も含む）・招待コードで止まる → 普通の登録の画面（エラーは押した時と同じ文言）
 */
export type ViaDecision = { kind: 'wait' } | { kind: 'start'; provider: DevOAuthProvider } | { kind: 'skip'; inviteError: 'invite_code_required' | 'invalid_invite_code' | null };

export function viaDecision(o: {
  via: DevOAuthProvider | null;
  providers: readonly DevOAuthProvider[] | null;
  invite: InviteInput;
  requirement: SignupInviteRequirement;
}): ViaDecision {
  if (!o.via) return { kind: 'skip', inviteError: null };
  if (o.providers === null) return { kind: 'wait' };
  if (!o.providers.includes(o.via)) return { kind: 'skip', inviteError: null };
  const inviteError = signupInviteError(o.requirement, o.invite);
  if (inviteError) return { kind: 'skip', inviteError };
  return { kind: 'start', provider: o.via };
}
